import React, { useContext, useEffect, useState } from 'react'
import { Dialog } from '@/components/ui/dialog'
import { GridColumn as Column, Grid, GridCellProps } from '@progress/kendo-react-grid'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { IntroducingBrokerDetailsDto } from 'api/Trading-Accounts/Types/IntroducingBrokerDetailsDto'
import AddButton from 'components/AddButton/AddButton'
import { Loading } from 'components/Loading'
import MoreButton from 'components/MoreButton/MoreButton'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { useAppDispatch, useAppSelector } from 'store'
import { fetchTickmillCompaniesAction } from 'store/identity/identity.actions'
import { useApiClient } from 'utils/api/ApiClient'
import { IdentityApiClient } from 'utils/api/identityApi'
import { PartnershipsApiClient } from 'utils/api/partnershipsApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { AuthSessionContext } from 'utils/AuthContext'
import { isEmpty } from 'utils/is-empty'
import { hasPermission } from 'utils/permissionsValidator'

import { IntroducingBrokerReferralRestrictionDto } from '../../../api/Trading-Accounts/Types/IntroducingBrokerReferralRestrictionDto'
import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal'
import { formatLocalDateUTC } from '../../../utils/date'
import {
  AddEditIBReferralRestrictionForm,
  AddEditIBReferralRestrictionFormValues,
} from './AddEditIBReferralRestrictionForm'
import {
  IntroducingBrokerNavigation,
  IntroducingBrokerTab,
} from './Navigation/IntroducingBrokerNavigation'

interface Props {
  data: IntroducingBrokerDetailsDto
}

const IntroducingBrokerReferralRestrictionsPage: React.FC<Props> = ({ data }) => {
  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []

  const partnershipsApiClient = useApiClient(PartnershipsApiClient)
  const identityApiClient = useApiClient(IdentityApiClient)

  const setToast = useContext(ToastContext)

  const dispatch = useAppDispatch()

  const tickmillCompanies = useAppSelector((state) => state.identity.tickmillCompanies)

  const [showAddEditIBEntity, setAddEditIBEntity] = useState<{
    entity?: IntroducingBrokerReferralRestrictionDto
    visible: boolean
  }>({
    visible: false,
  })

  const [entityToDelete, setEntityToDelete] = useState<{
    data: IntroducingBrokerReferralRestrictionDto | null
    visible: boolean
  }>({
    data: null,
    visible: false,
  })

  const { callback, forceRefresh } = useCallbackWithForceRefresh(() => {
    return partnershipsApiClient.getIntroducingBrokerReferralExceptions(data.id)
  }, [data.id]) // eslint-disable-line react-hooks/exhaustive-deps
  const { data: referralsRestrictions, isLoading } = useFetchOne(callback)

  useEffect(() => {
    if (isEmpty(tickmillCompanies)) {
      dispatch(fetchTickmillCompaniesAction(identityApiClient))
    }
  }, [data.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleDeleteConfirmation = (entity: IntroducingBrokerReferralRestrictionDto) => {
    setEntityToDelete({ data: entity, visible: true })
  }

  const handleDelete = async () => {
    if (entityToDelete.data) {
      try {
        await partnershipsApiClient.deleteIntroducingBrokerReferralException(
          data.id,
          entityToDelete.data.tickmillCompany.id
        )
        setToast(successToast('Referral restriction deleted.'))
        forceRefresh()
        handleConfirmationCancel()
      } catch (error) {
        console.error(error)
        handleConfirmationCancel()
      }
    }
  }

  const handleConfirmationCancel = () => {
    setEntityToDelete({ visible: false, data: null })
  }

  const onSubmit = async (values: AddEditIBReferralRestrictionFormValues) => {
    await partnershipsApiClient.addIntroducingBrokerReferralException(data.id, values)
    setToast(successToast('Entity added.'))
    setAddEditIBEntity({ entity: undefined, visible: false })
    forceRefresh()
  }

  return (
    <React.Fragment>
      {entityToDelete.data && (
        <ConfirmationModal
          size='sm'
          yesButtonText='Yes, delete'
          onYes={handleDelete}
          onCancel={() => setEntityToDelete({ data: null, visible: false })}
          visible={entityToDelete.visible}
          renderTitle={
            <>
              Are you sure you want to delete the restriction for the following entity:{' '}
              <strong>{entityToDelete.data.tickmillCompany.name}</strong>
            </>
          }
        />
      )}
      {showAddEditIBEntity.visible && (
        <Dialog
          open={showAddEditIBEntity.visible}
          onOpenChange={(open) =>
            !open && setAddEditIBEntity({ visible: false, entity: undefined })
          }
        >
          <AddEditIBReferralRestrictionForm
            entity={showAddEditIBEntity.entity}
            tickmillCompanies={tickmillCompanies}
            onSubmit={onSubmit}
          />
        </Dialog>
      )}
      <IntroducingBrokerNavigation
        data={data}
        isEdit={false}
        tab={IntroducingBrokerTab.REFERRAL_RESTRICTIONS}
      />
      <AddButton
        text='Add Referral Restriction'
        onClick={() => setAddEditIBEntity({ visible: true })}
        disabled={
          !hasPermission(permissions, PermissionsEnum.CanCreateIntroducingBrokerReferralRestriction)
        }
        data-test='add-entity-button'
      />
      <Loading isLoading={isLoading}>
        <Grid
          data={referralsRestrictions}
          total={(referralsRestrictions || []).length}
          style={{ zIndex: 0 }}
        >
          <Column field='tickmillCompany.name' title='Entity' />
          <Column
            title='Created Date'
            cell={(gridCellProps) => (
              <td>{formatLocalDateUTC(gridCellProps.dataItem.createdDate, true)}</td>
            )}
          />
          <Column field='user.name' title='User' />
          <Column
            width={50}
            title=''
            filterCell={() => null}
            cell={(gridCellProps: GridCellProps) => (
              <td>
                <MoreButton
                  items={[
                    {
                      name: 'Delete',
                      disabled: !hasPermission(
                        permissions,
                        PermissionsEnum.CanDeleteIntroducingBrokerReferralRestriction
                      ),
                      onClick: () => handleDeleteConfirmation(gridCellProps.dataItem),
                    },
                  ]}
                />
              </td>
            )}
          />
        </Grid>
      </Loading>
    </React.Fragment>
  )
}

export default IntroducingBrokerReferralRestrictionsPage
